import { GameId } from '@common'
import { AnyAction, Middleware, Reducer, isAnyOf, Dispatch } from '@reduxjs/toolkit'
import { Matcher } from '@reduxjs/toolkit/dist/tsHelpers'
import { v4 } from 'uuid'

import { api } from '@/api'
import { syncWithDraft } from '@/features/checkout/checkoutSlice'
import { RootState } from '@/store'
import logger from '@/utils/logger'

import { isClient } from './utils/isClient'

const GET_INIT_STATE = '&_GET_INIT_STATE'
const SEND_INIT_STATE = '&_SEND_INIT_STATE'
const RECEIVE_INIT_STATE = '&_RECEIVE_INIT_STATE'

type Config = {
  channel: string
  matchers: Matcher<unknown>[]
}

const config: Config = {
  channel: 'sc-store-state-sync',
  matchers: [api.endpoints.updateDraft.matchFulfilled],
}

type Stamp = {
  $uuid: string
  $wuid: string
  $isSync: boolean
  $gameId?: GameId
}

type StampedAction = Stamp & AnyAction

type MessageListenerConfig = {
  channel: BroadcastChannel
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>
  getState: () => RootState
}

const WINDOW_STATE_SYNC_ID = v4()

function createStampedAction(action: AnyAction, game?: GameId): StampedAction {
  // strip out non-serializable properties
  return JSON.parse(
    JSON.stringify({
      ...action,
      $uuid: v4(),
      $wuid: WINDOW_STATE_SYNC_ID,
      $isSync: false,
      $gameId: game,
    }),
  )
}

class MessageListener {
  private lastUuid = ''
  private isSynced = false
  private tabs: Record<string, boolean> = {}
  private messageChannel
  private dispatch
  private getState

  constructor({ channel, dispatch, getState }: MessageListenerConfig) {
    this.messageChannel = channel
    this.dispatch = dispatch
    this.getState = getState
    this.messageChannel.onmessage = this.handleOnMessage
  }

  private handleOnMessage = (message: MessageEvent<StampedAction>) => {
    if (typeof window === 'undefined') {
      // skip message handling on server-side
      return
    }

    const { data: stampedAction } = message

    if (stampedAction.$wuid === WINDOW_STATE_SYNC_ID) {
      // action originated from this window
      return
    }

    if (stampedAction.$gameId && stampedAction.$gameId !== this.getState().common.currentGame) {
      // action originated from another storefront
      return
    }

    if (stampedAction.$uuid && stampedAction.$uuid !== this.lastUuid) {
      if (stampedAction.type === GET_INIT_STATE && !this.tabs[stampedAction.$wuid]) {
        this.tabs[stampedAction.$wuid] = true
        this.dispatch(sendInitState())
      } else if (stampedAction.type === SEND_INIT_STATE && !this.tabs[stampedAction.$wuid]) {
        if (!this.isSynced) {
          this.isSynced = true
          this.dispatch(receiveInitState(stampedAction.payload))
        }
      } else if (isAnyOf(...config.matchers)(stampedAction)) {
        logger.debug('Syncing action in the current tab...')
        this.lastUuid = stampedAction.$uuid

        if (api.endpoints.updateDraft.matchFulfilled(stampedAction)) {
          // handle updateDraft separately
          this.dispatch(api.util.updateQueryData('getDraft', stampedAction.$gameId, () => stampedAction.payload))
          this.dispatch(syncWithDraft({ draft: stampedAction.payload, market: null }))
        } else {
          // handle other actions if needed with:
          // this.dispatch({ ...stampedAction, $isSync: true })
        }
      }
    }
  }
}

const getInitState = (): AnyAction => ({ type: GET_INIT_STATE })
const sendInitState = (): AnyAction => ({ type: SEND_INIT_STATE })
const receiveInitState = (state: RootState): StampedAction => ({
  type: RECEIVE_INIT_STATE,
  payload: state,
  $uuid: '',
  $wuid: '',
  $isSync: false,
  $gameId: undefined,
})

function getBroadcastChannel(channel: string): BroadcastChannel | null {
  if (isClient && 'BroadcastChannel' in window) {
    return new BroadcastChannel(channel)
  }

  return null
}

export function createStateSyncMiddleware(): Middleware {
  let messageListener: MessageListener | null = null
  const channel = getBroadcastChannel(config.channel)

  return ({ getState, dispatch }) =>
    (next) =>
    (action) => {
      if (channel === null) {
        // skip middleware logic on server-side & unsupported browsers
        return next(action)
      }
      // initialize message listener
      if (!messageListener) {
        messageListener = new MessageListener({ channel, dispatch, getState })
      }
      // ensure actions have a UUID before syncing
      if (!action.$uuid) {
        const currentGame = getState().common.currentGame
        const stampedAction = createStampedAction(action, currentGame)
        try {
          if (isAnyOf(...config.matchers)(stampedAction)) {
            logger.debug('Syncing action to other tabs...')
            channel.postMessage(stampedAction)
          }
        } catch (e) {
          logger.debug("Your browser doesn't support cross-tab communication")
        }
      }
      return next(action)
    }
}

export const withReduxStateSync =
  (appReducer: Reducer): Reducer =>
  (state, action) => {
    if (action.type === RECEIVE_INIT_STATE) {
      return (prevState: RootState, nextState: RootState) => nextState
    }
    return appReducer(state, action)
  }

export const initState = (store: { dispatch: (action: AnyAction) => void }) => {
  store.dispatch(getInitState())
}
